import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { Vacancy } from 'clients/AutoCompleteClient/types';
import type { JobImprovedGetDetailOutput } from 'clients/ReachoutClient/ReachoutClient.types';
import type { Candidate } from 'model/Candidate/Candidate';

import { useReachoutMessageGenerationLanguage } from '@/features/reachout-message-generation';
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { CandidateOutreachSendMethod, RJCommunicationHubAction } from 'clients/CampaignsClient/CampaignsClient.types';
import { reachoutClient } from 'clients/ReachoutClient/ReachoutClient';
import { queryClient } from 'config/queryClient';
import { useGTM } from 'hooks/gtm/useGTM';
import { useEnrichedVacanciesQuery } from 'hooks/reachout/useEnrichedVacanciesQuery';
import { useAutoProcessJobPreviewVacancy } from 'hooks/shared/useAutoProcessJobPreviewVacancy';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useGenerateReachoutMessageMutation } from 'shared/legacy/hooks/useGenerateReachoutMessageMutation';
import { useGetJobImprovedQuery } from 'shared/legacy/hooks/useGetJobImprovedQuery';
import { getJobImprovedCacheKey } from 'shared/legacy/hooks/useJobImprovedQuery';
import { useRJActivityLogsQuery } from 'shared/legacy/hooks/useRJActivityLogsQuery';
import { useRobinJrSelectVacancy } from 'shared/legacy/hooks/useRobinJrSelectVacancy';
import { useCampaignStore } from 'stores/campaign/campaign.store';
import { QueryKey } from 'types/query';
import { summarizeCandidateForAI } from 'utils/candidates/summarizeCandidateForAI';
import { getVacancyName } from 'utils/vacancyEnrichedUtils';
import { OutreachOrigin } from '@/shared/lib/outreach-origin';

type ContentRef = { message: string; subject: string } | null;

export const useOperations = ({ candidate, initialVacancy }: { candidate: Candidate; initialVacancy?: Vacancy }) => {
  const { dictionary } = useContext(LocalizationContext);

  const [selectedVacancy, setSelectedVacancy] = useState<Vacancy | null>(initialVacancy || null);
  const [hasUserChangedSelection, setHasUserChangedSelection] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [improvedJob, setImprovedJob] = useState<JobImprovedGetDetailOutput | null>(null);
  const [isLoadingImprovedJob, setIsLoadingImprovedJob] = useState(false);
  const contentRef = useRef<ContentRef>(null);

  const { data: atsVacancyList = [] } = useEnrichedVacanciesQuery();
  const { selected, setSelected: setSelectedAts, setSelectedType, isProcessingVacancy } = useRobinJrSelectVacancy();
  const { mutateAsync: autoProcessJobPreviewVacancy, isLoading: isProcessingJobPreviewVacancy } =
    useAutoProcessJobPreviewVacancy();
  const { mutateAsync: generateReachoutMessage, isLoading: isGeneratingMessage } = useGenerateReachoutMessageMutation();
  const gtm = useGTM();
  const aiLanguage = useReachoutMessageGenerationLanguage();
  const campaign = useCampaignStore((s) => s.campaign);

  const rjActivityLogsQuery = useRJActivityLogsQuery({
    es_person_id: candidate.es_person_id,
    campaign_id: candidate.campaignId,
  });

  const currentRJCommunicationHubAttempt = rjActivityLogsQuery.data;
  const aiSummary = summarizeCandidateForAI(candidate);

  const isResuming =
    !!currentRJCommunicationHubAttempt &&
    selectedVacancy?.db_id === Number(currentRJCommunicationHubAttempt.job_improved_id);

  const pausedVacancy = (() => {
    const job_improved_id = currentRJCommunicationHubAttempt?.job_improved_id;
    return atsVacancyList.find((ats) => ats.db_id === Number(job_improved_id)) || null;
  })();

  const campaignConnectedAts = useMemo(() => {
    if (!campaign?.connected_ats) return null;

    const enrichedMatch = atsVacancyList.find((vacancy) => vacancy.job_ats_id === campaign.connected_ats?.job_ats_id);
    if (enrichedMatch) return enrichedMatch;

    return {
      ...campaign.connected_ats,
      name: getVacancyName(campaign.connected_ats),
      value: campaign.connected_ats.job_ats_id,
    };
  }, [campaign?.connected_ats, atsVacancyList]);

  useEffect(() => {
    // RJ communication hub only supports ATS vacancies, not indeed
    setSelectedType('ats');
  }, [setSelectedType]);

  const vacancyToSelect = useMemo(() => {
    if (initialVacancy) return initialVacancy;
    if (hasUserChangedSelection) return selected;
    if (pausedVacancy) return pausedVacancy;
    if (campaignConnectedAts) return campaignConnectedAts;
    return selected;
  }, [initialVacancy, hasUserChangedSelection, selected, pausedVacancy, campaignConnectedAts]);

  const showLoadingProcessingVacancy =
    isProcessingVacancy ||
    isProcessingJobPreviewVacancy ||
    rjActivityLogsQuery.isLoading ||
    isLoadingImprovedJob ||
    rjActivityLogsQuery.isFetching;

  const fetchImprovedJob = useCallback(async () => {
    setIsLoadingImprovedJob(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: getJobImprovedCacheKey(Number(selectedVacancy?.db_id)),
        queryFn: () => reachoutClient.getJobImproved(Number(selectedVacancy?.db_id)).then((res) => res.data),
      });
      setImprovedJob(data);
      return data;
    } catch (error) {
      console.error('Failed to fetch improved job:', error);
      setImprovedJob(null);
      throw error;
    } finally {
      setIsLoadingImprovedJob(false);
    }
  }, [selectedVacancy]);

  const computeGeneratedSubject = useCallback(
    (jobData?: JobImprovedGetDetailOutput | null) => {
      const job = jobData ?? improvedJob;
      if (!job) return undefined;

      console.log('job', job);

      const jobTitle = job.job_details?.job_title || undefined;
      const rawSalaryRange = job.job_details?.salary_range?.trim();
      const salaryRange =
        rawSalaryRange && rawSalaryRange !== ''
          ? rawSalaryRange
              .split('-')
              .map((value) => (value.trim().includes('€') ? value.trim() : `€${value.trim()}`))
              .join('-')
          : undefined;
      const rawCity = job.employment_requirements?.city?.trim() || job.employment_requirements?.address?.trim();
      const city = rawCity ? rawCity.charAt(0).toUpperCase() + rawCity.slice(1) : undefined;

      return dictionary.companyIsHiring({
        jobTitle,
        salaryRange,
        city,
      });
    },
    [dictionary, improvedJob],
  );

  const refetchQueryBeforeClose = useCallback(() => {
    rjActivityLogsQuery.refetch();
    queryClient.invalidateQueries([
      QueryKey.useCandidateHistoryQuery,
      { candidateEsPersonId: candidate.es_person_id, status: candidate.status },
    ]);
    queryClient.invalidateQueries([
      QueryKey.useCandidateActivityLogQuery,
      { candidateEsPersonId: candidate.es_person_id, status: candidate.status },
    ]);
  }, [candidate.es_person_id, candidate.status, rjActivityLogsQuery]);

  const generateMessage = useCallback(async () => {
    if (!selectedVacancy) throw new Error('No vacancy selected');

    let improveJobPreviewPromise;
    if (selectedVacancy.is_improved && selectedVacancy.db_id) {
      improveJobPreviewPromise = autoProcessJobPreviewVacancy(selectedVacancy);
    }

    const data = await generateReachoutMessage({
      vacancy: selectedVacancy,
      candidate: { es_person_id: candidate.es_person_id, resume: aiSummary },
      outreach_type: CandidateOutreachSendMethod.Email,
      language: aiLanguage,
      origin: OutreachOrigin.rjCommunicationSingle,
    });

    if (improveJobPreviewPromise) {
      await improveJobPreviewPromise;
    }

    contentRef.current = data;

    let jobForSubject = improvedJob;
    if (!jobForSubject && selectedVacancy.db_id) {
      try {
        jobForSubject = await useGetJobImprovedQuery.fetchQuery(Number(selectedVacancy.db_id));
      } catch (error) {
        console.error('Failed to fetch improved job for subject:', error);
      }
    }

    const generatedSubject = computeGeneratedSubject(jobForSubject);
    if (generatedSubject) {
      contentRef.current.subject = generatedSubject;
    }

    return data;
  }, [
    selectedVacancy,
    generateReachoutMessage,
    candidate.es_person_id,
    aiSummary,
    computeGeneratedSubject,
    improvedJob,
    aiLanguage,
    autoProcessJobPreviewVacancy,
  ]);

  const handleResumePipeline = useCallback(
    async (handleOutreachModalRequestClose: () => void) => {
      setIsLoading(true);
      try {
        await campaignsClient.manageRJCommunicationHubPipeline({
          campaign_id: candidate.campaignId,
          es_person_id: candidate.es_person_id,
          action: RJCommunicationHubAction.Resume,
        });
        gtm.rjCommHubResumed();
        handleOutreachModalRequestClose();
        refetchQueryBeforeClose();
      } finally {
        setIsLoading(false);
      }
    },
    [candidate.campaignId, candidate.es_person_id, gtm, refetchQueryBeforeClose],
  );

  const handleSelectVacancy = useCallback(
    (vacancy: Vacancy | null) => {
      setHasUserChangedSelection(true);
      setSelectedVacancy(vacancy);
      setSelectedAts(vacancy);
    },
    [setSelectedAts, setSelectedVacancy],
  );

  return {
    selectedVacancy,
    setSelectedVacancy,
    hasUserChangedSelection,
    isLoading,
    setIsLoading,
    contentRef,
    atsVacancyList,
    selectedAts: selected,
    isProcessingVacancy,
    isGeneratingMessage,
    rjActivityLogsQuery,
    currentRJCommunicationHubAttempt,
    isResuming,
    pausedVacancy,
    vacancyToSelect,
    showLoadingProcessingVacancy,
    refetchQueryBeforeClose,
    generateMessage,
    handleResumePipeline,
    handleSelectVacancy,
    improvedJob,
    isLoadingImprovedJob,
    fetchImprovedJob,
    computeGeneratedSubject,
  };
};
